import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, ImportWizard } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/riskcategories';

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  description: { type: 'text', odataField: 'Description' },
  sortOrder: { type: 'number', odataField: 'SortOrder' },
};
const SEARCH_FIELDS = ['TextId', 'Description'];

function toIntOrZero(value) {
  if (value === '' || value == null) return 0;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function RiskCategoriesPage() {
  usePageMeta('Risk Kategorileri', 'Risk başlıklarını kategori bazında yönetin.');

  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'SortOrder',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openNew = () => navigate('/risk-categories/new');
  const openEdit = (row = selected) => { if (!row) return; navigate(`/risk-categories/${row.id}?edit=1`, { state: { row } }); };
  const openView = (row = selected) => { if (!row) return; navigate(`/risk-categories/${row.id}`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Risk kategorisi başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('description', { header: 'Kategori', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('sortOrder', {
      header: 'Sıra', meta: { filterType: 'number' },
      cell: (info) => { const v = info.getValue(); return (v === null || v === undefined || v === '') ? '-' : v; },
    }),
    columnHelper.accessor('createdDate', {
      header: 'Oluşturma', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('SortOrder', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Kod' },
        { field: 'description', headerName: 'Kategori' },
        { field: 'sortOrder', headerName: 'Sıra' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `risk-kategorileri_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma ===
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const importRowToPayload = (row) => ({
    textId: importPick(row, ['Kod', 'textId', 'Code', 'TextId']),
    description: importPick(row, ['Kategori', 'Açıklama', 'Aciklama', 'description', 'Description', 'Ad', 'Name']),
    sortOrder: importPick(row, ['Sıra', 'Sira', 'sortOrder', 'SortOrder']),
  });
  const importValidateRow = (p) => (p.textId ? null : 'Kod boş');
  const importSubmitRow = async (p) => {
    try {
      const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ textId: p.textId, description: p.description, sortOrder: toIntOrZero(p.sortOrder) }) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => { exportToExcel([{ 'Kod': 'RK01', 'Kategori': 'Örnek Kategori', 'Sıra': '1' }], null, 'risk-kategorisi_sablon'); };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod veya kategori ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        {canAdd && <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>}
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Risk Kategorileri"
        subtitle="Risk başlıklarını kategori bazında yönetin."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Risk Kategorisi</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openView}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.description || selected?.textId}"</strong> risk kategorisini silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} risk kategorisi içe aktarıldı`); fetchData(); } }}
        title="Risk Kategorisi İçe Aktar"
        helpText="Şablondaki kolon başlıklarını koruyun. Zorunlu alan: Kod. Kategori ve Sıra opsiyonel."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
